import { Button } from "@/components/ui/Button";
import { updateTouristPanelAvailability } from "@/lib/touristPanelApi";
import type { TouristPanelDisplay } from "@/types/touristPanel";
import { useCallback, useEffect, useState } from "react";

type Props = {
  workstationCode: string;
  display: TouristPanelDisplay;
  onSaved: (display: TouristPanelDisplay) => void;
};

export function TouristPanelSameDayCutoffEditor({ workstationCode, display, onSaved }: Props) {
  const [enabled, setEnabled] = useState(display.availability.same_day_cutoff.enabled);
  const [cutoffTime, setCutoffTime] = useState(display.availability.same_day_cutoff.cutoff_time);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setEnabled(display.availability.same_day_cutoff.enabled);
    setCutoffTime(display.availability.same_day_cutoff.cutoff_time);
    setSuccess(false);
  }, [display.availability.same_day_cutoff]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const availability = await updateTouristPanelAvailability(workstationCode, {
        same_day_cutoff_enabled: enabled,
        same_day_cutoff_time: cutoffTime,
      });
      onSaved({ ...display, availability });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }, [cutoffTime, display, enabled, onSaved, workstationCode]);

  return (
    <section className="mx-auto mb-8 max-w-2xl rounded-xl border border-separator bg-surface p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-label">Same-Day-Cutoff</h2>
      <p className="mt-1 text-sm text-label-secondary">
        Ab welcher Uhrzeit Gäste am Anreisetag nicht mehr für heute online buchen können.
      </p>

      <div className="mt-4 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              setSuccess(false);
            }}
          />
          Same-Day-Buchungssperre aktivieren
        </label>

        {enabled ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Cutoff-Uhrzeit
            <input
              type="time"
              className="eh-input"
              value={cutoffTime}
              onChange={(e) => {
                setCutoffTime(e.target.value);
                setSuccess(false);
              }}
              required
            />
            <span className="text-xs text-label-secondary">
              Ab dieser Uhrzeit am heutigen Tag ist eine Anreise am selben Tag nicht mehr möglich.
            </span>
          </label>
        ) : null}
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {success ? <p className="mt-4 text-sm text-green-700">Gespeichert.</p> : null}

      <div className="mt-5">
        <Button type="button" onClick={() => void onSave()} disabled={saving}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
      </div>
    </section>
  );
}
